import { cloneElement, useState, type FormEvent, type ReactElement } from "react";
import { Clock } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { PHONE, CallButton, TextButton, Reveal } from "@/components/site-chrome";
import { SERVICE_OPTIONS, validateServiceRequest } from "@/lib/service-options";

const field = "mt-1 w-full rounded-sm border border-input bg-background px-3 py-2.5 text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring";
const lbl = "label-mono text-muted-foreground";

export function RequestService({ defaultService = "", contactLink = false }: { defaultService?: string; contactLink?: boolean }) {
  const [status, setStatus] = useState<"idle" | "sending" | "done" | "error">("idle");
  const [errors, setErrors] = useState<Record<string, string>>({});

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const honeypot = (e.currentTarget.elements.namedItem("hp-website") as HTMLInputElement | null)?.value ?? "";
    if (honeypot) {
      setStatus("done");
      return;
    }
    const raw = Object.fromEntries(new FormData(e.currentTarget)) as Record<string, string>;
    const parsed = validateServiceRequest(raw);
    if (!parsed.ok) {
      setErrors(parsed.errors);
      return;
    }
    setErrors({});
    setStatus("sending");
    try {
      // The serializer for server functions stays out of the first load.
      const { submitServiceRequest } = await import("@/lib/service-request.functions");
      await submitServiceRequest({ data: parsed.data });
      setStatus("done");
    } catch {
      setStatus("error");
    }
  }

  return (
    <section id="contact" className="bg-charcoal-deep py-24" aria-labelledby="request-h">
      <div className="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-3">
        <Reveal className="lg:col-span-2">
          <div className="flex items-center gap-3"><span className="rule-orange" aria-hidden /><p className="label-mono text-primary">Request service</p></div>
          <h2 id="request-h" className="mt-4 text-5xl font-black md:text-6xl">Not an Emergency? Request Service</h2>
          <p className="mt-4 max-w-2xl text-foreground/80">For non-urgent repairs, inspections, or fleet service, send a quick request and Josh will get back to you. Broken down right now? Call or text {PHONE}.</p>
          {contactLink && <Link to="/contact" className="label-mono mt-3 inline-block text-primary hover:underline">All contact options →</Link>}

          <div className="cut-corner mt-8 border border-border bg-card p-6 sm:p-8">
            {status === "done" ? (
              <p role="status" className="font-display text-3xl font-bold uppercase">Thanks! Josh will reach out soon. <span className="block mt-2 font-sans text-base normal-case text-foreground/80">Need help now? Call or text {PHONE}.</span></p>
            ) : (
              <form
                onSubmit={onSubmit}
                noValidate
                className="grid gap-5 sm:grid-cols-2"
                toolname="request_service"
                tooldescription="Request non-urgent mobile auto repair, a pre-purchase inspection, or fleet service from 406 Mobile Mechanic in Great Falls, Montana. Josh replies during open hours, Monday through Sunday, 8 AM to 9 PM. For a breakdown right now, call or text 406-799-0095 instead of submitting this form."
              >
                <input id="hp-website" type="text" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
                <F label="Name *" err={errors["name"]}><input name="name" required maxLength={100} autoComplete="name" className={field} toolparamdescription="Customer full name." /></F>
                <F label="Phone *" err={errors["phone"]}><input name="phone" type="tel" required maxLength={30} autoComplete="tel" className={field} toolparamdescription="Phone number Josh can call or text. Digits, spaces, parentheses, plus, or dashes." /></F>
                <F label="Email (optional)" err={errors["email"]}><input name="email" type="email" maxLength={255} autoComplete="email" className={field} toolparamdescription="Optional email address for the reply. Leave empty if unknown." /></F>
                <F label="Vehicle Year/Make/Model" err={errors["vehicle"]}><input name="vehicle" maxLength={120} placeholder="e.g. 2014 Ford F-150" className={field} toolparamdescription="Optional vehicle year, make, and model. Example: 2014 Ford F-150." /></F>
                <F label="Service Needed" err={errors["service"]}>
                  <select name="service" defaultValue={defaultService} className={field} toolparamdescription="Optional service. Leave blank if unsure. Choices: Mobile Auto Repair, Diagnostics, Electrical, Brakes & Suspension, Pre-Purchase Inspection, Oil Change/Tune-Up, Transmission, Heating & AC, Fleet Service, Other.">
                    <option value="">Select a service</option>
                    {SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </F>
                <F label="Location/Town" err={errors["location"]}><input name="location" maxLength={120} placeholder="e.g. Black Eagle" className={field} toolparamdescription="Optional town or location where the vehicle is, such as Great Falls or Black Eagle." /></F>
                <div className="sm:col-span-2"><F label="Message" err={errors["message"]}><textarea name="message" rows={4} maxLength={2000} className={field} toolparamdescription="Optional description of the problem, vehicle symptoms, or scheduling notes." /></F></div>
                <div className="sm:col-span-2 flex flex-wrap items-center gap-4">
                  <button type="submit" disabled={status === "sending"} className="rounded-sm bg-primary px-7 py-3 font-display text-xl font-extrabold uppercase tracking-wide text-primary-foreground transition-transform hover:-translate-y-0.5 disabled:opacity-60">
                    {status === "sending" ? "Sending…" : "Send Request"}
                  </button>
                  {status === "error" && <p role="alert" className="text-sm text-destructive">Something went wrong. Please call or text {PHONE}.</p>}
                </div>
              </form>
            )}
          </div>
        </Reveal>

        <Reveal className="lg:pt-40">
          <aside className="topo border-2 border-primary bg-card p-6 sm:p-8">
            <p className="label-mono text-primary">Fastest way to reach Josh</p>
            <div className="mt-4 flex items-center gap-3"><Clock className="h-6 w-6 text-primary" aria-hidden /><p className="font-display text-3xl font-black uppercase">Mon–Sun · 8 AM – 9 PM</p></div>
            <p className="mt-3 text-sm text-foreground/80">Broken down or need help today? Call or text for the quickest response.</p>
            <div className="mt-6 flex flex-col gap-3"><CallButton size="lg" /><TextButton size="lg" label="Text Josh" /></div>
          </aside>
        </Reveal>
      </div>
    </section>
  );
}

function F({ label, err, children }: { label: string; err?: string | undefined; children: ReactElement<{ name?: string; "aria-invalid"?: boolean; "aria-describedby"?: string }> }) {
  const name = children.props.name;
  const errId = err && name ? `${name}-error` : undefined;
  return (
    <label className="block">
      <span className={lbl}>{label}</span>
      {cloneElement(children, err && errId ? { "aria-invalid": true, "aria-describedby": errId } : {})}
      {err && <span id={errId} className="mt-1 block text-sm text-destructive">{err}</span>}
    </label>
  );
}
